/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useMemo } from 'react';
import { useNetworkStatus } from './useNetworkStatus';
import { Platform } from 'react-native';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '@airasia-rn/account/hooks';
import { useAppConfig } from '@airasia-rn/ui';
import {
  RecommendationApiService,
  socialKeys,
  useSocialFeed,
} from '@airasia-rn/social';
import { useSearchHistory } from '@airasia-rn/social/hooks';
import type { MovementsHomeRequest } from '@airasia-rn/social';
import { useNearestAirportsCodes } from '@airasia-rn/flights/hooks';

import { isRecommendationTabsBlock } from '../models/homepageBlockHelpers';
import type { HomepageBlock } from '../models/homepageBlockTypes';
import { isRecommendationNetworkError } from './recommendationErrorUtils';
import { useForYouFeed } from './useForYouFeed';
import { usePromotions } from './usePromotions';
import { useMovementsV4LayoutEnabled } from '../pages/HomePage/useHomePageRuntime';

const normalizeTabId = (tabId: string) => String(tabId).toLowerCase();

const MOVEMENTS_FEED_LIMITS = {
  maxPostsPerSection: 3,
  maxSections: 3,
  maxChips: 3,
} as const;

const createMovementsQueryOptions = ({
  apiService,
  getAccessToken,
  locale,
  market,
  appVersion,
  recommendationHashKey,
  isConnected,
  searchHistory,
  location,
}: {
  apiService: RecommendationApiService;
  getAccessToken: () => Promise<string | undefined>;
  locale: string;
  market: string;
  appVersion: string;
  recommendationHashKey: string;
  isConnected: boolean;
  searchHistory?: MovementsHomeRequest['searchHistory'];
  location?: MovementsHomeRequest['location'];
}) =>
  queryOptions({
    queryKey: [
      ...socialKeys.feed(locale, market, MOVEMENTS_FEED_LIMITS),
      isConnected,
    ],
    queryFn: async () => {
      const accessToken = (await getAccessToken()) ?? '';
      if (!accessToken) {
        throw new Error('Missing access token');
      }

      return apiService.fetchMovementsHome({
        accessToken,
        locale,
        market,
        platform: Platform.OS,
        channelHash: recommendationHashKey,
        appVersion,
        ...MOVEMENTS_FEED_LIMITS,
        searchHistory,
        location,
      });
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: 3,
  });

export const useHomepageRecommendations = (blocks: HomepageBlock[] = []) => {
  const queryClient = useQueryClient();
  const { config } = useAppConfig();
  const socialAccessToken = useAccessToken('social');

  const recommendationTabIds = useMemo(() => {
    const tabIds = new Set<string>();

    blocks.forEach((block) => {
      if (!isRecommendationTabsBlock(block)) {
        return;
      }

      block.data.tabs.forEach((tab) => {
        tabIds.add(normalizeTabId(tab.id));
      });
    });

    return tabIds;
  }, [blocks]);

  const hasForYouFeed = recommendationTabIds.has('for_you');
  const hasPromotionsFeed = recommendationTabIds.has('promotions');
  const hasMovementsFeed = recommendationTabIds.has('movements');

  const { data: searchHistory, isPending: isSearchHistoryPending } =
    useSearchHistory();
  const nearestAirportQuery = useNearestAirportsCodes();

  const { isConnected, isNetworkStatusKnown } = useNetworkStatus();
  const isV4LayoutEnabled = useMovementsV4LayoutEnabled();

  const {
    refresh: refreshForYou,
    refreshErrorInfo: forYouRefreshErrorInfo,
    loadMoreError: forYouLoadMoreError,
    loadMoreErrorInfo: forYouLoadMoreErrorInfo,
  } = useForYouFeed({ enabled: hasForYouFeed });
  const {
    refresh: refreshPromotions,
    refreshErrorInfo: promotionsRefreshErrorInfo,
    loadMoreError: promotionsLoadMoreError,
    loadMoreErrorInfo: promotionsLoadMoreErrorInfo,
  } = usePromotions({
    enabled: hasPromotionsFeed,
  });

  const isRecommendationOffline =
    isRecommendationNetworkError(forYouRefreshErrorInfo) ||
    (forYouLoadMoreError &&
      isRecommendationNetworkError(forYouLoadMoreErrorInfo)) ||
    isRecommendationNetworkError(promotionsRefreshErrorInfo) ||
    (promotionsLoadMoreError &&
      isRecommendationNetworkError(promotionsLoadMoreErrorInfo));

  const recommendationApiService = useMemo(
    () => new RecommendationApiService(config.env ?? 'stg'),
    [config.env],
  );

  const locale = config.locale ?? 'en-gb';
  const market = config.geoId ?? 'MY';
  const appVersion = config.appVersion ?? '';
  const recommendationHashKey =
    process.env.EXPO_PUBLIC_RECOMMENDATION_HASH_KEY ?? '';

  const location = useMemo(() => {
    const data = nearestAirportQuery.data;
    const nearestAirportLatLong = data?.latlong ?? '';
    const parts = nearestAirportLatLong.split(',');
    const lat = parts.length === 2 ? Number(parts[0].trim()) : NaN;
    const lon = parts.length === 2 ? Number(parts[1].trim()) : NaN;

    return {
      country: config.geoId || data?.country || 'MY',
      city: data?.city ?? '',
      nearestAirport: data?.airport?.[0] ?? '',
      latitude: Number.isFinite(lat) ? lat : 0,
      longitude: Number.isFinite(lon) ? lon : 0,
    };
  }, [config.geoId, nearestAirportQuery.data]);

  // HomePage renders its blocks in a tightly-virtualized FlatList
  // (initialNumToRender/windowSize ~3 on native), so the movements tab's own
  // component isn't guaranteed to mount right away — it may only appear once
  // the user scrolls near it. This hook, in contrast, runs unconditionally on
  // Home mount, so it preloads movements data ahead of that delay, ensuring
  // it's already cached by the time the tab actually renders.
  //
  // Exactly one of the two preload calls below is active at a time, gated by
  // isV4LayoutEnabled, so v1 and v4 never fire together for the same tab.
  // The v1 branch owns its own token/search-history/location wiring because
  // createMovementsQueryOptions is a plain query-options factory with no
  // internal auth logic. The v4 branch reuses useSocialFeed() — the same hook
  // RecommendationTabsView calls — so both observers share one request under
  // socialKeys.feedV4(locale, market); it only needs `enabled` here since
  // useSocialFeed owns its own auth/enrichment/location logic. Both branches
  // still check isConnected/isNetworkStatusKnown explicitly because this repo
  // has no onlineManager wired to NetInfo, so react-query has no automatic
  // network awareness — useSocialFeed does not check network status itself.
  const isMovementsPreloadEnabled =
    hasMovementsFeed &&
    !isV4LayoutEnabled &&
    Boolean(recommendationHashKey) &&
    Boolean(socialAccessToken.data) &&
    socialAccessToken.isTokenValid &&
    isConnected &&
    isNetworkStatusKnown &&
    !isSearchHistoryPending;

  useQuery({
    ...createMovementsQueryOptions({
      apiService: recommendationApiService,
      getAccessToken: socialAccessToken.getToken,
      locale,
      market,
      appVersion,
      recommendationHashKey,
      isConnected,
      searchHistory: searchHistory ?? undefined,
      location,
    }),
    enabled: isMovementsPreloadEnabled,
  });

  const isV4MovementsPreloadEnabled =
    hasMovementsFeed &&
    isV4LayoutEnabled &&
    isConnected &&
    isNetworkStatusKnown;

  useSocialFeed({ enabled: isV4MovementsPreloadEnabled });

  const refreshAll = useCallback(async () => {
    await Promise.all([
      hasForYouFeed ? refreshForYou() : Promise.resolve(),
      hasPromotionsFeed ? refreshPromotions() : Promise.resolve(),
      hasMovementsFeed
        ? queryClient.invalidateQueries({
            queryKey: socialKeys.feedPrefix(),
          })
        : Promise.resolve(),
    ]);
  }, [
    hasForYouFeed,
    hasPromotionsFeed,
    hasMovementsFeed,
    queryClient,
    refreshForYou,
    refreshPromotions,
  ]);

  return {
    hasForYouFeed,
    hasPromotionsFeed,
    hasMovementsFeed,
    refreshAll,
    isRecommendationOffline,
  };
};
